import React, { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useApolloClient } from "@apollo/client";
import { Select } from "@outpave/ds";
import type { SelectOption } from "@outpave/ds";
import UserStatus from "../../enums/UserStatus";
import { GET_USERS } from "../../graphql/user";
import { DEFAULT_PAGINATION_SIZE } from "./Constants";
import { useSelectSearch } from "hooks/useSelectSearch";
import { fullName } from "utils/fullName";

export interface Employee {
  id: string;
  firstName: string;
  lastName?: string | null;
  email: string;
  hasPhysicalCard?: boolean;
  address?: Address | null;
  /**
   * Card issuance requires these, and the "cannot have a card created" notice
   * in EmployeeSearch is derived from them, so anything that drops them here
   * makes that notice permanent. Add new fields to `employeeFromUserNode`.
   */
  phone?: string | null;
  birthdate?: Date | null;
}

interface Address {
  addressName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipcode: string;
}

/**
 * The one place a GET_USERS node becomes an Employee. Exported so a caller
 * refetching a single user reuses it rather than writing a second copy, which
 * is exactly how `phone` and `birthdate` went missing: every field is
 * optional, so a mapping that drops one still type-checks.
 */
export const employeeFromUserNode = (node: Employee): Employee => ({
  id: node.id,
  firstName: node.firstName,
  lastName: node.lastName,
  email: node.email,
  hasPhysicalCard: node.hasPhysicalCard,
  address: node.address,
  phone: node.phone,
  birthdate: node.birthdate,
});

const getEmployeeLabel = (option: Employee) =>
  [fullName(option), option.email].filter(Boolean).join(" - ");

interface EmployeeSearchAutoCompleteProps {
  handleSelectEmployee: (selectedEmployee: Employee | null) => void;
  employee: Employee | null;
  title?: string;
  additionalVariables?: { [key: string]: unknown };
  errorText?: string;
  forceLoading?: boolean;
  required?: boolean;
  /** Formik's `handleBlur`, to mark the field touched. */
  handleBlur?: React.FocusEventHandler<HTMLInputElement>;
  /**
   * When set (including `[]`), only these employees appear; the directory query is skipped.
   */
  optionsFromTransactions?: Employee[];
  /**
   * Start the create-a-user flow. Omit it and the pinned row is not rendered,
   * which is how a caller's permission gate reaches this: the absence of the
   * callback is the absence of the affordance.
   */
  onAddUser?: () => void;
}

/**
 * The employee picker, in two modes.
 *
 * With `optionsFromTransactions` the list is fixed, so filtering is local and
 * the DS marks matches from its own input. Otherwise it searches the directory,
 * and `highlight` carries the term the RESULTS came back with rather than the
 * live input, which lags it by a round trip.
 *
 * The pinned `topAction` above the list is the caller's: pass `onAddUser` to
 * offer it, omit it to leave the row out. The permission that gates it lives
 * with the caller, not here.
 */
const EmployeeSearchAutoComplete: React.FC<EmployeeSearchAutoCompleteProps> = ({
  employee,
  handleSelectEmployee,
  title = "",
  additionalVariables = {},
  errorText = "",
  forceLoading = false,
  required = false,
  handleBlur = () => {},
  optionsFromTransactions,
  onAddUser,
}) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const isStatic = optionsFromTransactions !== undefined;
  // Options carry only `{ id, label }`; callers want the domain object back.
  const seenRef = useRef(new Map<string, Employee>());
  (optionsFromTransactions ?? []).forEach((e) => seenRef.current.set(e.id, e));

  const fetchPage = useCallback(
    async (query: string, after: string | null) => {
      const { data } = await client.query({
        query: GET_USERS,
        variables: {
          searchTerm: query,
          status: [UserStatus.ACTIVE, UserStatus.PENDING],
          first: DEFAULT_PAGINATION_SIZE,
          after: after ?? undefined,
          ...additionalVariables,
        },
        fetchPolicy: "network-only",
      });
      const connection = data?.users;
      const employees: Employee[] = (connection?.edges ?? []).map(
        ({ node }: { node: Employee }) => employeeFromUserNode(node),
      );
      employees.forEach((e) => seenRef.current.set(e.id, e));
      return {
        options: employees.map((e) => ({
          id: e.id,
          label: getEmployeeLabel(e),
        })),
        hasNextPage: connection?.pageInfo?.hasNextPage ?? false,
        endCursor: connection?.pageInfo?.endCursor ?? null,
      };
    },
    // additionalVariables and client cover the query inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, additionalVariables],
  );

  const seed = useMemo<SelectOption | null>(
    () =>
      employee?.id
        ? { id: employee.id, label: getEmployeeLabel(employee) }
        : null,
    [employee],
  );

  const search = useSelectSearch<SelectOption>({
    fetchPage,
    seed,
    skip: isStatic,
  });

  const staticOptions = useMemo<SelectOption[]>(
    () =>
      (optionsFromTransactions ?? []).map((e) => ({
        id: e.id,
        label: getEmployeeLabel(e),
      })),
    [optionsFromTransactions],
  );

  const onChange = (id: string | null) => {
    if (!id) {
      if (!isStatic) search.reset();
      handleSelectEmployee(null);
      return;
    }
    handleSelectEmployee(seenRef.current.get(id) ?? employee);
  };

  return (
    <Select
      label={title || undefined}
      aria-label={title ? undefined : "Employee"}
      searchable
      required={required}
      errorText={errorText || undefined}
      options={isStatic ? staticOptions : search.options}
      loading={forceLoading || (!isStatic && search.loading)}
      // Local mode marks from its own input; only server search needs the term.
      {...(isStatic
        ? {}
        : {
            highlight: search.query,
            onInputChange: search.onInputChange,
            onScrollEnd: search.onScrollEnd,
          })}
      value={employee?.id ?? null}
      // The DS closes the menu before firing this, so the drawer it opens
      // does not leave a dropdown hanging behind it.
      topAction={
        onAddUser
          ? { label: t("Add a new user"), onClick: onAddUser }
          : undefined
      }
      onChange={onChange}
      onBlur={handleBlur}
    />
  );
};

export default EmployeeSearchAutoComplete;
